import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardHeader,
    CardContent,
    Typography,
    Button,
    TextField,
    IconButton,
    CircularProgress,
    Divider,
    Alert,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import { Save, Add, Delete, Calculate } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import systemSettingsService from "../../services/systemSettingsService";
import PageHeader from "../../components/common/PageHeader";

const blankTier = { maxCbf: "", shippingRate: "", insuranceMin: "" };

const defaultRates = {
    minimumCbf: { cbf: 0, shippingRate: 0, insuranceMin: 0 },
    cbfTiers: [
        { maxCbf: 2, shippingRate: 24.99, insuranceMin: 5 },
        { maxCbf: 5, shippingRate: 39.99, insuranceMin: 15 },
        { maxCbf: 10, shippingRate: 79.99, insuranceMin: 15 },
    ],
    heavyWeightThresholdLb: 50,
    heavyHandlingFee: 50,
    insuranceRate: 0.0275,
    portHandlingRatePerCbf: 0.75,
    portHandlingRatePerLb: 0.05,
    portHandlingMinFee: 5,
    deliveryFee: 15,
    usdToCiRate: 1.2,
};

// Mirrors server/services/chargeCalculatorService.js exactly, so the "Test
// Estimate" preview below reflects what generating a real invoice would
// actually charge — this is not a separate/simplified formula. Every rate
// here (and the total this produces) is CI$; merchandiseValue is the
// exception — it's the customer's own purchase invoice value, always US$ —
// so it's converted via usdToCiRate before being used in the Insurance line.
const previewCharges = (rates, { cbf, weightLb, merchandiseValue, deliverySelected, customsDuties }) => {
    const n = (v) => Number(v) || 0;
    cbf = n(cbf);
    weightLb = n(weightLb);
    merchandiseValue = n(merchandiseValue);
    customsDuties = n(customsDuties);
    const merchandiseValueCi = merchandiseValue / (n(rates.usdToCiRate) || 1.2);

    // Minimum CBF is its own tier (own Shipping Rate + Min Insurance),
    // applied whenever actual CBF is at or under it — see
    // chargeCalculatorService.calculateCharges (this mirrors it exactly).
    const minCbfThreshold = n(rates.minimumCbf?.cbf);
    let shipping;
    let insuranceMin;
    if (minCbfThreshold > 0 && cbf <= minCbfThreshold) {
        shipping = n(rates.minimumCbf.shippingRate);
        insuranceMin = n(rates.minimumCbf.insuranceMin);
        cbf = minCbfThreshold;
    } else {
        const tiers = [...rates.cbfTiers].map((t) => ({ ...t, maxCbf: n(t.maxCbf) })).sort((a, b) => a.maxCbf - b.maxCbf);
        const tier = tiers.find((t) => cbf <= t.maxCbf) || tiers[tiers.length - 1];
        shipping = tier ? n(tier.shippingRate) : 0;
        insuranceMin = tier ? n(tier.insuranceMin) : 0;
    }

    const heavyHandling = weightLb > n(rates.heavyWeightThresholdLb) ? n(rates.heavyHandlingFee) : 0;

    const calculatedInsurance = (merchandiseValueCi + shipping) * n(rates.insuranceRate);
    const insurance = Math.max(calculatedInsurance, insuranceMin);

    const portHandlingCalc = Math.max(cbf * n(rates.portHandlingRatePerCbf), weightLb * n(rates.portHandlingRatePerLb));
    const portHandling = Math.max(portHandlingCalc, n(rates.portHandlingMinFee));

    const delivery = deliverySelected ? n(rates.deliveryFee) : 0;

    const round2 = (v) => Math.round(v * 100) / 100;
    const lines = [
        { description: "Shipping", amount: round2(shipping) },
        ...(heavyHandling > 0 ? [{ description: "Heavy Handling", amount: round2(heavyHandling) }] : []),
        { description: "Insurance", amount: round2(insurance) },
        { description: "Port & Handling", amount: round2(portHandling) },
        ...(delivery > 0 ? [{ description: "Delivery", amount: round2(delivery) }] : []),
        ...(customsDuties > 0 ? [{ description: "Customs Duties", amount: round2(customsDuties) }] : []),
    ];
    return { lines, total: round2(lines.reduce((s, l) => s + l.amount, 0)) };
};

const AdminInvoiceRates = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [rates, setRates] = useState(defaultRates);

    const [test, setTest] = useState({
        cbf: 3,
        weightLb: 40,
        merchandiseValue: 200,
        deliverySelected: true,
        customsDuties: 0,
    });

    useEffect(() => {
        systemSettingsService
            .get()
            .then(({ data }) => {
                const s = data.data.settings;
                if (s.invoiceRates) {
                    setRates({
                        minimumCbf: {
                            cbf: s.invoiceRates.minimumCbf?.cbf ?? defaultRates.minimumCbf.cbf,
                            shippingRate: s.invoiceRates.minimumCbf?.shippingRate ?? defaultRates.minimumCbf.shippingRate,
                            insuranceMin: s.invoiceRates.minimumCbf?.insuranceMin ?? defaultRates.minimumCbf.insuranceMin,
                        },
                        cbfTiers: s.invoiceRates.cbfTiers?.length ? s.invoiceRates.cbfTiers : defaultRates.cbfTiers,
                        heavyWeightThresholdLb: s.invoiceRates.heavyWeightThresholdLb ?? defaultRates.heavyWeightThresholdLb,
                        heavyHandlingFee: s.invoiceRates.heavyHandlingFee ?? defaultRates.heavyHandlingFee,
                        insuranceRate: s.invoiceRates.insuranceRate ?? defaultRates.insuranceRate,
                        portHandlingRatePerCbf: s.invoiceRates.portHandlingRatePerCbf ?? defaultRates.portHandlingRatePerCbf,
                        portHandlingRatePerLb: s.invoiceRates.portHandlingRatePerLb ?? defaultRates.portHandlingRatePerLb,
                        portHandlingMinFee: s.invoiceRates.portHandlingMinFee ?? defaultRates.portHandlingMinFee,
                        deliveryFee: s.invoiceRates.deliveryFee ?? defaultRates.deliveryFee,
                        usdToCiRate: s.invoiceRates.usdToCiRate ?? defaultRates.usdToCiRate,
                    });
                }
            })
            .catch(() => enqueueSnackbar("Failed to load invoice rates", { variant: "error" }))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setField = (field) => (e) => setRates((p) => ({ ...p, [field]: e.target.value }));
    const setMinCbfField = (field) => (e) =>
        setRates((p) => ({ ...p, minimumCbf: { ...p.minimumCbf, [field]: e.target.value } }));
    const setTierField = (idx, field) => (e) =>
        setRates((p) => ({
            ...p,
            cbfTiers: p.cbfTiers.map((t, i) => (i === idx ? { ...t, [field]: e.target.value } : t)),
        }));
    const addTier = () => setRates((p) => ({ ...p, cbfTiers: [...p.cbfTiers, { ...blankTier }] }));
    const removeTier = (idx) => setRates((p) => ({ ...p, cbfTiers: p.cbfTiers.filter((_, i) => i !== idx) }));

    const setTestField = (field) => (e) =>
        setTest((p) => ({ ...p, [field]: field === "deliverySelected" ? e.target.checked : e.target.value }));

    const handleSave = async () => {
        setSaving(true);
        try {
            await systemSettingsService.update({
                invoiceRates: {
                    minimumCbf: {
                        cbf: Number(rates.minimumCbf.cbf) || 0,
                        shippingRate: Number(rates.minimumCbf.shippingRate) || 0,
                        insuranceMin: Number(rates.minimumCbf.insuranceMin) || 0,
                    },
                    cbfTiers: rates.cbfTiers.map((t) => ({
                        maxCbf: Number(t.maxCbf) || 0,
                        shippingRate: Number(t.shippingRate) || 0,
                        insuranceMin: Number(t.insuranceMin) || 0,
                    })),
                    heavyWeightThresholdLb: Number(rates.heavyWeightThresholdLb) || 0,
                    heavyHandlingFee: Number(rates.heavyHandlingFee) || 0,
                    insuranceRate: Number(rates.insuranceRate) || 0,
                    portHandlingRatePerCbf: Number(rates.portHandlingRatePerCbf) || 0,
                    portHandlingRatePerLb: Number(rates.portHandlingRatePerLb) || 0,
                    portHandlingMinFee: Number(rates.portHandlingMinFee) || 0,
                    deliveryFee: Number(rates.deliveryFee) || 0,
                    usdToCiRate: Number(rates.usdToCiRate) || 1.2,
                },
            });
            enqueueSnackbar("Invoice rates updated", { variant: "success" });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to save invoice rates", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    const preview = previewCharges(rates, test);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <PageHeader
                title="Invoice Rates"
                subtitle="Configure the formula used to auto-calculate Shipping, Insurance, Port & Handling, Heavy Handling and Delivery charges when generating an invoice from a shipment"
            />

            <Alert severity="info" sx={{ borderRadius: 2 }}>
                These are live settings, not hardcoded values — every number here is read directly by the invoice
                charge calculator each time an admin auto-generates an invoice from a shipment.
            </Alert>

            <Card>
                <CardHeader
                    title={<Typography fontWeight={700} fontSize={14}>CBF Shipping Tiers</Typography>}
                    subheader="The tier whose Max CBF first covers the shipment's total CBF sets both the Shipping rate and the Insurance minimum for that shipment."
                    action={
                        <Button size="small" startIcon={<Add />} onClick={addTier}>
                            Add Tier
                        </Button>
                    }
                />
                <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            Minimum CBF tier — used whenever the shipment's actual CBF is at or under this, instead of
                            the tiers below. Leave CBF at 0 to disable (always use the tiers below).
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                                gap: 1.5,
                            }}
                        >
                            <TextField
                                size="small"
                                label="Minimum CBF"
                                type="number"
                                value={rates.minimumCbf.cbf}
                                onChange={setMinCbfField("cbf")}
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                            <TextField
                                size="small"
                                label="Shipping Rate ($)"
                                type="number"
                                value={rates.minimumCbf.shippingRate}
                                onChange={setMinCbfField("shippingRate")}
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                            <TextField
                                size="small"
                                label="Insurance Min ($)"
                                type="number"
                                value={rates.minimumCbf.insuranceMin}
                                onChange={setMinCbfField("insuranceMin")}
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                        </Box>
                    </Box>
                    <Divider />
                    {rates.cbfTiers.map((tier, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr auto" },
                                gap: 1.5,
                                alignItems: "center",
                            }}
                        >
                            <TextField
                                size="small"
                                label="Max CBF"
                                type="number"
                                value={tier.maxCbf}
                                onChange={setTierField(idx, "maxCbf")}
                                inputProps={{ min: 0 }}
                            />
                            <TextField
                                size="small"
                                label="Shipping Rate ($)"
                                type="number"
                                value={tier.shippingRate}
                                onChange={setTierField(idx, "shippingRate")}
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                            <TextField
                                size="small"
                                label="Insurance Min ($)"
                                type="number"
                                value={tier.insuranceMin}
                                onChange={setTierField(idx, "insuranceMin")}
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                            <IconButton size="small" color="error" onClick={() => removeTier(idx)}>
                                <Delete fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                </CardContent>
            </Card>

            <Card>
                <CardHeader title={<Typography fontWeight={700} fontSize={14}>Heavy Handling</Typography>} />
                <CardContent>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                        <TextField
                            label="Weight Threshold (lb)"
                            type="number"
                            value={rates.heavyWeightThresholdLb}
                            onChange={setField("heavyWeightThresholdLb")}
                            helperText="Charged when total weight exceeds this"
                            inputProps={{ min: 0 }}
                        />
                        <TextField
                            label="Heavy Handling Fee ($)"
                            type="number"
                            value={rates.heavyHandlingFee}
                            onChange={setField("heavyHandlingFee")}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Box>
                </CardContent>
            </Card>

            <Card>
                <CardHeader
                    title={<Typography fontWeight={700} fontSize={14}>Currency Conversion</Typography>}
                    subheader="Every rate/fee on this page (and the invoice total they produce) is CI$. Merchandise Value is the exception — it's the customer's own purchase invoice, always US$ — so it's converted using this rate before the Insurance line is calculated."
                />
                <CardContent>
                    <TextField
                        label="1 CI$ = ? US$"
                        type="number"
                        value={rates.usdToCiRate}
                        onChange={setField("usdToCiRate")}
                        helperText="Official Cayman dollar peg is 1.20. Change only if Waypoint uses a different in-house rate."
                        inputProps={{ min: 0, step: 0.0001 }}
                        sx={{ maxWidth: 320 }}
                    />
                </CardContent>
            </Card>

            <Card>
                <CardHeader title={<Typography fontWeight={700} fontSize={14}>Insurance</Typography>} />
                <CardContent>
                    <TextField
                        label="Insurance Rate"
                        type="number"
                        value={rates.insuranceRate}
                        onChange={setField("insuranceRate")}
                        helperText="Decimal, e.g. 0.0275 = 2.75%. Charged on (Merchandise Value + Shipping), or the tier's Insurance Min, whichever is higher."
                        inputProps={{ min: 0, step: 0.0001 }}
                        sx={{ maxWidth: 320 }}
                    />
                </CardContent>
            </Card>

            <Card>
                <CardHeader title={<Typography fontWeight={700} fontSize={14}>Port & Handling</Typography>} />
                <CardContent>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
                        <TextField
                            label="Rate per CBF ($)"
                            type="number"
                            value={rates.portHandlingRatePerCbf}
                            onChange={setField("portHandlingRatePerCbf")}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                        <TextField
                            label="Rate per lb ($)"
                            type="number"
                            value={rates.portHandlingRatePerLb}
                            onChange={setField("portHandlingRatePerLb")}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                        <TextField
                            label="Minimum Fee ($)"
                            type="number"
                            value={rates.portHandlingMinFee}
                            onChange={setField("portHandlingMinFee")}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                        Charged: whichever is higher of (CBF × rate/CBF), (weight × rate/lb), or the minimum fee.
                    </Typography>
                </CardContent>
            </Card>

            <Card>
                <CardHeader title={<Typography fontWeight={700} fontSize={14}>Delivery</Typography>} />
                <CardContent>
                    <TextField
                        label="Delivery Fee ($)"
                        type="number"
                        value={rates.deliveryFee}
                        onChange={setField("deliveryFee")}
                        helperText="Charged only when the shipment has delivery selected"
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{ maxWidth: 320 }}
                    />
                </CardContent>
            </Card>

            <Box>
                <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}>
                    {saving ? <CircularProgress size={18} color="inherit" /> : "Save Rates"}
                </Button>
            </Box>

            <Divider />

            <Card sx={{ bgcolor: "action.hover" }}>
                <CardHeader
                    title={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Calculate fontSize="small" />
                            <Typography fontWeight={700} fontSize={14}>Test Estimate</Typography>
                        </Box>
                    }
                    subheader="Plug in sample numbers to see exactly what an invoice would charge with the rates above (uses the unsaved values on this page)"
                />
                <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
                        <TextField
                            size="small"
                            label="Total CBF"
                            type="number"
                            value={test.cbf}
                            onChange={setTestField("cbf")}
                        />
                        <TextField
                            size="small"
                            label="Total Weight (lb)"
                            type="number"
                            value={test.weightLb}
                            onChange={setTestField("weightLb")}
                        />
                        <TextField
                            size="small"
                            label="Merchandise Value (US$)"
                            type="number"
                            value={test.merchandiseValue}
                            onChange={setTestField("merchandiseValue")}
                            helperText="Purchase invoice value, in US$ — converted to CI$ before use"
                        />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <TextField
                            size="small"
                            label="Customs Duties ($)"
                            type="number"
                            value={test.customsDuties}
                            onChange={setTestField("customsDuties")}
                            sx={{ flex: 1 }}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={test.deliverySelected} onChange={setTestField("deliverySelected")} />}
                            label="Delivery"
                        />
                    </Box>

                    <Box sx={{ bgcolor: "background.paper", borderRadius: 2, p: 2, border: "1px solid", borderColor: "divider" }}>
                        {preview.lines.map((l) => (
                            <Box key={l.description} sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">{l.description}</Typography>
                                <Typography variant="body2" fontWeight={600}>CI${l.amount.toFixed(2)}</Typography>
                            </Box>
                        ))}
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography fontWeight={700}>Total</Typography>
                            <Typography fontWeight={700} color="primary.main">CI${preview.total.toFixed(2)}</Typography>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default AdminInvoiceRates;
